import React, { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Flex, Text } from '@chakra-ui/react';
import FitTabs from '../../components/shared/tabs/Tabs';
import { darkMode, lightMode } from '../../theme/config';
import { ListComponent } from '../../components/shared/ListTab';
import { loadModelsRequest } from '../../store/modelsSlice';
import { FaPlus } from 'react-icons/fa';
import { useColorModeValue } from '../../components/ui/color-mode';
import { listPath } from '../../utils/constants/insightsPath';
import { formatComplaintsData, formatProductsData } from '../../utils/dataFormatters';
import { Header } from '../../components/shared/IndexHeader';
import { setActiveTabForCustomer } from '../../store/customerSlice';
import UpdateComplaintDialog from './UpdateComplaintDialog';
import CustomerInfoSection from './CustomerInfoSection';
import { useCustomerActions } from './useCustomerActions';
import {
  productColumns,
  complaintColumns,
  complaintOptions,
  getProductActions,
  getComplaintActions
} from './customerConstants';

const CustomerDetailsTabs = () => {
  const activeTab = useSelector((state) => state.customer.activeTab);
  const { models } = useSelector((state) => state.models || { models: [] });

  const borderColor = useColorModeValue(lightMode.menuIconHoverColor, darkMode.containerBorderColor);
  const textColor = useColorModeValue(lightMode.inputTextColor, darkMode.inputTextColor);
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(loadModelsRequest());
  }, [dispatch]);

  const {
    updateComplaintDialog,
    handleEditProduct,
    handleRaiseComplaint,
    handleEditComplaint,
    closeUpdateComplaintDialog,
    customer
  } = useCustomerActions();

  const productFilters = useMemo(() => ({ userId: customer.userId }), [customer.userId]);
  const complaintFilters = useMemo(() => ({ userId: customer.userId }), [customer.userId]);

  const productList = useSelector((state) => state.list[listPath.PRODUCT] || { rows: [] });
  const complaintsList = useSelector((state) => state.list[listPath.COMPLAINTS] || { rows: [] });

  const {
    productCategories,
    productSubcategories,
    serviceRequestCategories,
    serviceRequestSubcategories
  } = useSelector((state) => state.categories);

  const handleTabChange = (newTab) => {
    dispatch(setActiveTabForCustomer(newTab.value));
  };

  if (!customer) {
    return (
      <Box p={4}>
        <Text color="red.500">Error: No customer data available.</Text>
      </Box>
    );
  }

  const { firstName, lastName, email } = customer;
  const productActions = getProductActions(handleEditProduct, handleRaiseComplaint);
  const complaintActions = getComplaintActions(handleEditComplaint);

  const tabs = [
    {
      value: 'details',
      label: 'Details',
      panel: <CustomerInfoSection customer={customer} />,
    },
    {
      value: 'products',
      label: `Products (${productList?.totalCount || 0})`,
      panel: (
        <ListComponent
          path={listPath.PRODUCT}
          borderColor={borderColor}
          searchPlaceholder="Search by customer..."
          columns={productColumns}
          actions={productActions}
          formatData={(rows) => formatProductsData(
            rows,
            productCategories,
            productSubcategories,
            models,
          )}
          categoryOptions={productCategories}
          subCategoryOptions={productSubcategories}
          userId={customer.userId}
          initialFilters={productFilters}
          hideExport
          hideDateRange
        />
      ),
    },
    {
      value: 'complaints',
      label: `Complaints (${complaintsList?.totalCount || 0})`,
      panel: (
        <ListComponent
          borderColor={borderColor}
          path={listPath.COMPLAINTS}
          searchPlaceholder="Search Complaints..."
          columns={complaintColumns}
          actions={complaintActions}
          formatData={(rows) => formatComplaintsData(
            rows,
            serviceRequestCategories,
            serviceRequestSubcategories,
          )}
          statusOptions={complaintOptions}
          categoryOptions={serviceRequestCategories}
          subCategoryOptions={serviceRequestSubcategories}
          userId={customer.userId}
          initialFilters={complaintFilters}
          hideExport
          hideDateRange
          showSearchBar={true}
        />
      ),
    },
  ];

  return (
    <Box>
      <Flex direction="column" mb={6}>
        {activeTab === "products" ? (
          <Header
            title={firstName && firstName + " " + lastName}
            buttonText="Add Product"
            navigateTo="/product/add"
            textColor="blue"
            activeTab={activeTab}
            customerEmail={email}
          />
        ) : activeTab === "complaints" ? (
          <Header
            title={firstName && firstName + " " + lastName}
            buttonIcon={<FaPlus style={{ paddingRight: "5px" }} />}
            navigateTo="/complaint/add"
            textColor="blue"
            activeTab={activeTab}
          />
        ) : (
          <Header title={firstName && firstName + " " + lastName} />
        )}
        <Text fontSize="md" color={textColor} fontWeight="semibold">
          {email}
        </Text>
      </Flex>

      <FitTabs page="customer" tabs={tabs} value={activeTab} onChange={handleTabChange} fitted="false" width="100%" variant="enclosed" />

      <UpdateComplaintDialog isOpen={updateComplaintDialog} onClose={closeUpdateComplaintDialog} />
    </Box>
  );
};

export default CustomerDetailsTabs;